import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, } from "@ant-design/icons";
import { LEADERSHIP_PAGE_SIZE, LEADERSHIP_STATUS_OPTIONS } from "./leadership.constants";
import {
  useAddLeadershipAttentionMutation,
  useDeleteLeadershipAttentionMutation,
  useLeadershipAttentionQuery,
  useUpdateLeadershipAttentionMutation,
} from "./leadership.hooks";
import { formatLeadershipDate, getLeadershipStatusColor } from "./leadership.utils";
import DataState from "../../../components/DataState/DataState";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";



export function LeadershipAttentionSection({ projectId, canManage }) {
  const [newLeadershipItemText, setNewLeadershipItemText] = useState("");
  const [editingLeadershipRowId, setEditingLeadershipRowId] = useState(null);
  const [editingLeadershipRowData, setEditingLeadershipRowData] = useState({
    description: "",
    remarks: "",
    status: "Open",
  });
  const [leadershipCurrentPage, setLeadershipCurrentPage] = useState(1);

  const leadershipQuery = useLeadershipAttentionQuery(projectId);
  const addMutation = useAddLeadershipAttentionMutation(projectId);
  const updateMutation = useUpdateLeadershipAttentionMutation(projectId);
  const deleteMutation = useDeleteLeadershipAttentionMutation(projectId);

  const leadershipItems = leadershipQuery.data?.items ?? [];
  const flaggedCount = leadershipQuery.data?.flaggedCount ?? 0;

  // Loading on the first render of a new project and while a retry is in flight.
  const leadershipStatus =
    leadershipQuery.isLoading || (leadershipQuery.isError && leadershipQuery.isFetching)
      ? "loading"
      : leadershipQuery.isError
        ? "error"
        : "ready";

  const openLeadershipCount = useMemo(
    () =>
      leadershipItems.filter((item) => {
        const normalizedStatus = String(item.status || "").trim().toLowerCase();
        return normalizedStatus !== "closed" && normalizedStatus !== "resolved";
      }).length,
    [leadershipItems],
  );

  const paginatedLeadershipItems = useMemo(
    () =>
      leadershipItems.slice(
        (leadershipCurrentPage - 1) * LEADERSHIP_PAGE_SIZE,
        leadershipCurrentPage * LEADERSHIP_PAGE_SIZE,
      ),
    [leadershipCurrentPage, leadershipItems],
  );

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(leadershipItems.length / LEADERSHIP_PAGE_SIZE));
    if (leadershipCurrentPage > totalPages) {
      setLeadershipCurrentPage(totalPages);
    }
  }, [leadershipItems.length, leadershipCurrentPage]);

  const handleAddLeadershipItem = async () => {
    if (!newLeadershipItemText.trim()) {
      message.warning("Please enter an item");
      return;
    }

    const lines = newLeadershipItemText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);

    if (lines.length === 0) return;

    try {
      await addMutation.mutateAsync(lines);
      setNewLeadershipItemText("");
      message.success(`Added ${lines.length} leadership item${lines.length > 1 ? "s" : ""}`);
    } catch (error) {
      console.error("Error saving leadership attention items:", error);
      message.error("Failed to save leadership attention items. Please try again.");
    }
  };

  const handleEditLeadershipItem = (itemId) => {
    const item = leadershipItems.find((entry) => entry.id === itemId);
    if (!item) return;

    setEditingLeadershipRowId(itemId);
    setEditingLeadershipRowData({
      description: item.description,
      remarks: item.remarks || "",
      status: item.status || "Open",
    });
  };

  const handleUpdateEditingLeadershipField = (field, value) => {
    setEditingLeadershipRowData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelLeadershipEdit = () => {
    setEditingLeadershipRowId(null);
    setEditingLeadershipRowData({ description: "", remarks: "", status: "Open" });
  };

  const handleSaveLeadershipEdit = async () => {
    if (!editingLeadershipRowId) return;
    if (!editingLeadershipRowData.description.trim()) {
      message.warning("Description cannot be empty");
      return;
    }

    const currentItem = leadershipItems.find((item) => item.id === editingLeadershipRowId);
    const normalizedStatus = editingLeadershipRowData.status.trim().toLowerCase();
    const nowIso = new Date().toISOString();

    const payload= {
      description: editingLeadershipRowData.description.trim(),
      remarks: editingLeadershipRowData.remarks.trim(),
      status: editingLeadershipRowData.status,
    };

    if (normalizedStatus === "closed") {
      payload.closedOn = nowIso;
    }

    if (normalizedStatus === "open") {
      payload.openedOn = currentItem?.openedOn || nowIso;
    }

    try {
      await updateMutation.mutateAsync({ attentionId: editingLeadershipRowId, payload });
      handleCancelLeadershipEdit();
      message.success("Leadership item updated!");
    } catch (error) {
      console.error("Error updating leadership attention item:", error);
      message.error("Failed to update leadership attention item. Please try again.");
    }
  };

  const handleDeleteLeadershipItem = async (itemId) => {
    try {
      await deleteMutation.mutateAsync(itemId);
      message.success("Leadership item deleted!");
    } catch (error) {
      console.error("Error deleting leadership attention item:", error);
      message.error("Failed to delete item. Please try again.");
    }
  };

  return (
    <Card
      style={{ borderRadius: 14, border: "1px solid #e6ecf5", boxShadow: "0 4px 14px rgba(16,24,40,0.03)" }}
      styles={{ body: { padding: 16 } }}
    >
      <DataState
        status={leadershipStatus}
        errorTitle="Couldn't load leadership attention items"
        error={leadershipQuery.error?.message || "Failed to fetch leadership attention items"}
        onRetry={() => leadershipQuery.refetch()}
        skeletonRows={4}
      >
      <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Leadership Attention
          </Typography.Title>
          
        </div>
      </div>
      

      {canManage && (
        <Card
          style={{ borderRadius: 10, border: "1px solid #d6e4ff", background: "#f5f8ff", marginBottom: 12 }}
          styles={{ body: { padding: 12 } }}
        >
          <Input.TextArea
            rows={3}
            value={newLeadershipItemText}
            onChange={(event) => setNewLeadershipItemText(event.target.value)}
            placeholder="Add leadership attention note..."
            style={{ marginBottom: 10 }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {newLeadershipItemText.length} characters
            </Typography.Text>
            <Space>
              <Button type="primary" onClick={handleAddLeadershipItem} loading={addMutation.isPending}>
                Save
              </Button>
              <Button onClick={() => setNewLeadershipItemText("")}>Clear</Button>
            </Space>
          </div>
        </Card>
      )}

      <Card
        style={{
          borderRadius: 14,
          border: "1px solid #f0f0f0",
        }}
        styles={{ body: { padding: 16 } }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Leadership Attention</span>
            <Tag
              color="orange"
              style={{
                borderRadius: 12,
                padding: "0 8px",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {openLeadershipCount} open
            </Tag>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          {leadershipItems.length === 0 && (
            <Empty description="No leadership attention items yet" style={{ marginTop: 16 }} />
          )}

          {leadershipItems.length > 0 && (
            <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "hidden" }}>
              <div style={{ fontSize: 13, width: "100%" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "28% 20% 12% 14% 14% 12%",
                    gap: 8,
                    paddingBottom: 12,
                    marginBottom: 12,
                    borderBottom: "2px solid #f0f0f0",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#595959",
                    textTransform: "uppercase",
                  }}
                >
                  <div style={{ padding: "0 8px" }}>Description</div>
                  <div style={{ padding: "0 8px" }}>Remarks</div>
                  <div style={{ padding: "0 8px" }}>Status</div>
                  <div style={{ padding: "0 8px" }}>Opened On</div>
                  <div style={{ padding: "0 8px" }}>Closed On</div>
                  <div style={{ padding: "0 8px" }}>Actions</div>
                </div>

                {paginatedLeadershipItems.map((item) => {
                  const isRowEditing = editingLeadershipRowId === item.id;

                  return (
                    <div
                      key={item.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "28% 20% 12% 14% 14% 12%",
                        gap: 8,
                        paddingBottom: 12,
                        marginBottom: 12,
                        borderBottom: "1px solid #f0f0f0",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        {isRowEditing ? (
                          <Input
                            value={editingLeadershipRowData.description}
                            onChange={(event) =>
                              handleUpdateEditingLeadershipField("description", event.target.value)
                            }
                            style={{ height: 35 }}
                          />
                        ) : (
                          <Typography.Text
                            style={{
                              fontSize: 13,
                              color: "#262626",
                              display: "block",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.description}
                          </Typography.Text>
                        )}
                      </div>

                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        {isRowEditing ? (
                          <Input
                            value={editingLeadershipRowData.remarks}
                            onChange={(event) =>
                              handleUpdateEditingLeadershipField("remarks", event.target.value)
                            }
                            placeholder="Enter remarks"
                            size="small"
                            style={{ height: 35 }}
                          />
                        ) : (
                          <Typography.Text
                            style={{
                              fontSize: 12,
                              color: "#262626",
                              display: "block",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.remarks || "-"}
                          </Typography.Text>
                        )}
                      </div>

                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        {isRowEditing ? (
                          <Select
                            value={editingLeadershipRowData.status}
                            onChange={(value) =>
                              handleUpdateEditingLeadershipField("status", value)
                            }
                            options={LEADERSHIP_STATUS_OPTIONS}
                            size="small"
                            style={{ width: "100%", height: 35 }}
                          />
                        ) : (
                          <Tag color={getLeadershipStatusColor(item.status)} style={{ fontSize: 11, fontWeight: 600 }}>
                            {item.status || "Open"}
                          </Tag>
                        )}
                      </div>

                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        <Typography.Text style={{ fontSize: 12, color: "#262626" }}>
                          {formatLeadershipDate(item.openedOn)}
                        </Typography.Text>
                      </div>

                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        <Typography.Text style={{ fontSize: 12, color: "#262626" }}>
                          {formatLeadershipDate(item.closedOn)}
                        </Typography.Text>
                      </div>

                      <div style={{ padding: "4px 8px", minWidth: 0 }}>
                        <Space size={4} wrap>
                          {isRowEditing ? (
                            <>
                              <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={handleSaveLeadershipEdit}
                                loading={updateMutation.isPending}
                              />
                              <Button
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={handleCancelLeadershipEdit}
                              />
                            </>
                          ) : (
                            <>
                              {canManage && (
                                <>
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                                    onClick={() => handleEditLeadershipItem(item.id)}
                                    title="Edit"
                                  />
                                  <Popconfirm
                                    title="Delete Item"
                                    description="Are you sure you want to delete this item?"
                                    onConfirm={() => handleDeleteLeadershipItem(item.id)}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <Button
                                      size="small"
                                      type="text"
                                      icon={<DeleteOutlined />}
                                      danger
                                      title="Delete"
                                      loading={deleteMutation.isPending}
                                    />
                                  </Popconfirm>
                                </>
                              )}
                            </>
                          )}
                        </Space>
                      </div>
                    </div>
                  );
                })}
              </div>

              {leadershipItems.length > LEADERSHIP_PAGE_SIZE && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <Pagination
                    current={leadershipCurrentPage}
                    pageSize={LEADERSHIP_PAGE_SIZE}
                    total={leadershipItems.length}
                    onChange={(page) => setLeadershipCurrentPage(page)}
                    showSizeChanger={false}
                    size="small"
                  />
                </div>
              )}
            </div>
          )}
        </Space>
      </Card>
      </>
      </DataState>
    </Card>
  );
}
